<template>
  <b-modal
    v-model="isVisible"
    :title="$t('general.audioPreview')"
    hide-footer
    centered
    size="lg"
    content-class="audio-preview-modal-content"
    @hidden="onHidden"
  >
    <div class="audio-preview-modal">
      <div class="audio-container">
        <div v-if="audioUrl && !loadError" class="audio-player-wrapper">
          <div class="audio-player-visual">
            <feather-icon icon="MusicIcon" size="40" />
          </div>
          <audio
            ref="audioPlayer"
            class="audio-player"
            controls
            autoplay
            @error="handleAudioError"
          >
            <source :src="audioUrl" :type="audioType">
            {{ $t('general.audioNotSupported') }}
          </audio>
        </div>
        <div v-else-if="loadError" class="error-message">
          <feather-icon icon="AlertTriangleIcon" size="40" />
          <p>{{ $t('general.audioLoadError') }}</p>
        </div>
        <div v-else class="loading-message">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p>{{ $t('general.loading') }}</p>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'AudioPreviewModal',

  components: {
    BModal,
    BSpinner,
    FeatherIcon,
  },

  props: {
    // Audio URL to play
    audioUrl: {
      type: String,
      default: '',
    },
    // Audio MIME type
    audioType: {
      type: String,
      default: 'audio/mpeg',
    },
    // Whether the modal is visible
    value: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadError: false,
    }
  },

  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },

  watch: {
    value(newValue) {
      if (newValue) {
        // Reset state when modal is opened
        this.loadError = false
        if (this.$refs.audioPlayer) {
          this.$refs.audioPlayer.currentTime = 0
        }
      }
    },

    audioUrl() {
      // Reset error state when audio URL changes
      this.loadError = false
    },
  },

  methods: {
    handleAudioError() {
      this.loadError = true
    },

    onHidden() {
      // Pause audio when modal is closed
      if (this.$refs.audioPlayer) {
        this.$refs.audioPlayer.pause()
      }
      this.$emit('hidden')
    },
  },
}
</script>

<style lang="scss" scoped>
.audio-preview-modal {
  .audio-container {
    position: relative;
    width: 100%;
    background-color: #f8f9fa;
    border-radius: 8px;
    overflow: hidden;
    padding: 1.5rem;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;

    .audio-player-wrapper {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5rem;
    }

    .audio-player-visual {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background-color: var(--primary);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .audio-player {
      width: 100%;
      outline: none;
    }

    .error-message {
      padding: 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #f8d7da;
      color: #721c24;

      p {
        margin-top: 1rem;
        font-weight: 500;
      }
    }

    .loading-message {
      padding: 2rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #f8f9fa;
      color: #6c757d;

      p {
        margin-top: 1rem;
        font-weight: 500;
      }
    }
  }
}
</style>

<style lang="scss">
.audio-preview-modal-content {
  border-radius: 12px;
  overflow: hidden;

  .modal-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e9ecef;

    .modal-title {
      font-weight: 600;
      font-size: 1.1rem;
    }
  }

  .modal-body {
    padding: 1.5rem;
  }
}
</style>
